import { Avatar, Grid, MenuItem } from "@mui/material";
import { useEffect, useState, useCallback, useMemo } from "react";
import { ActionButton } from "../../../../components/Button";
import Dialog from "../../../../components/Dialog";
import { IAddEditDialogProps } from "../../../../components/Dialog";
import TextField from "../../../../components/TextField";
import { useLanguageContext } from "../../../../contexts/LanguageContext";
import ISystemSettings from "../../../../interfaces/ISystemSettings";
import ValidationHelper from '../../../../helpers/ValidationHelper';
import DefaultConstants from "../../../../data/Constants";
import FormHelper from "../../../../helpers/FormHelper";
import SystemSettingsService from '../../../../services/SystemSettingsService';
import SelectField from "../../../../components/SelectField";
import StringHelper from "../../../../helpers/StringHelper";
import { useEnvironmentContext } from "../../../../contexts/EnvironmentContext";
import IEnvironment from "../../../../interfaces/IEnvironment";


const EditDialog: React.FunctionComponent<IAddEditDialogProps<ISystemSettings>> = ({
    open,
    handleClose,
    data,
    handleListRefresh = () => {}
}) => {
    const { environment } = useEnvironmentContext();
    const { translate } = useLanguageContext();

    const [value, setValue] = useState<string | number | boolean>('');


    useEffect(() => {
        if (open && data) {
            setValue(data.value);
        }

        // eslint-disable-next-line
    }, [open]);


    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        let formData = FormHelper.Create(event);

        if (data?.id) {
            formData = FormHelper.Add(formData, 'id', data.id);
        }

        SystemSettingsService.Edit(formData).then(() => {
            handleClose();
            handleListRefresh();
        });

        // eslint-disable-next-line
    }, [data]);


    const dialogActions = useMemo(() => {
        return [
            <ActionButton action='cancel' onClick={handleClose} />,
            <ActionButton action='save' type='submit' />
        ];
        // eslint-disable-next-line
    }, [translate]);


    const buildFieldValue = useCallback(() => {
        const label = translate('value');
        const name = 'value';

        if (data?.type.match(/list/i)) {
            const key = StringHelper.FirstLetterLowerCase(data.type) as keyof(IEnvironment);
            const list = (environment[key] ?? []) as any[];
            
            return (
                <SelectField 
                    label={label} 
                    name={name} 
                    value={value}
                    onChange={e => setValue(e.target.value)}
                    customData={true}
                    required={false}
                    data={list.map(row => {
                        return <MenuItem value={row.description} key={row.id}>
                            {row.images && <Avatar src={row.images.icon} alt={row.description} className='selectfield-default-menuitem-avatar' />}
                            {row.description}
                        </MenuItem>;
                    })}
                />
            );
        }

        return (
            <TextField 
                type={data?.type || 'text'}
                label={label} 
                name={name} 
                value={value} 
                onChange={e => ValidationHelper.MaxLength(e.target.value, 200, setValue)}
                required={false}
            />
        );

        // eslint-disable-next-line
    }, [data, value]);


    return (
        <Dialog
            open={open}
            handleClose={handleClose}
            title={translate(data ? 'edit' : 'add')}
            maxWidth='sm'
            fullWidth={true}
            fullHeight={true}
            dialogActions={dialogActions}
            onSubmit={handleSubmit}
            className='dialogactions-spacebetween'
        >
            <Grid container columnSpacing={DefaultConstants.gridColumnSpacing}>
                <Grid item xs={12}>
                    <TextField 
                        label={translate('category')} 
                        name='category' 
                        value={data?._category ?? '-'}
                        disabled={true}
                        required={false}
                    />
                </Grid>
                
                <Grid item xs={12}>
                    <TextField 
                        label={translate('description')} 
                        name='description' 
                        value={data?._description ?? '-'}
                        disabled={true}
                        required={false}
                        multiline={true}
                        rows={4}
                    />
                </Grid>

                <Grid item xs={12}>
                    {
                        buildFieldValue()
                    }
                </Grid>
            </Grid>
        </Dialog>
    );
}

export default EditDialog;